import { isAddressLikeQueryValue } from '@/utils/urlPrivacy';

const FORWARDED_QUERY_KEYS = new Set([
  'src',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
]);

const DESTINATION_QUERY_KEYS = new Set(['mode', 'redirect', 'autostart', 'source', 'src']);

/**
 * Forwarded query values longer than this are dropped, not truncated.
 * A cut value can still be an address.
 */
export const FREE_PAGE_QUERY_VALUE_MAX_LENGTH = 100;

function readSearch(search: string): URLSearchParams {
  const raw = search.startsWith('?') ? search.slice(1) : search;
  return new URLSearchParams(raw);
}

/** Drop address-like values and values over the length cap. Empty values stay out. */
function acceptedFreeQueryValue(value: string | null): string | null {
  if (!value) return null;
  if (isAddressLikeQueryValue(value)) return null;
  if (value.length > FREE_PAGE_QUERY_VALUE_MAX_LENGTH) return null;
  return value;
}

function isDestinationQueryKey(key: string): boolean {
  return DESTINATION_QUERY_KEYS.has(key) || (key.startsWith('utm_') && FORWARDED_QUERY_KEYS.has(key));
}

/**
 * src plus the utm allowlist, in arrival order, with src first when it is safe.
 * Other keys, including utm_email and any other utm_*, stay behind.
 * A value that contains @ (raw or decoded, so %40 counts), starts with mailto:,
 * or is longer than FREE_PAGE_QUERY_VALUE_MAX_LENGTH is dropped.
 */
export function freePageAttribution(search: string): Record<string, string> {
  const params = readSearch(search);
  const result: Record<string, string> = {};
  const src = acceptedFreeQueryValue(params.get('src'));
  if (src) result.src = src;
  for (const key of params.keys()) {
    if (key === 'src' || !FORWARDED_QUERY_KEYS.has(key) || result[key]) continue;
    const value = acceptedFreeQueryValue(params.get(key));
    if (value) result[key] = value;
  }
  return result;
}

export function withFreePageAttribution(href: string, search: string): string {
  if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return href;
  const extra = freePageAttribution(search);
  const keys = Object.keys(extra);
  if (keys.length === 0) return href;
  const hashIndex = href.indexOf('#');
  const hash = hashIndex >= 0 ? href.slice(hashIndex) : '';
  const withoutHash = hashIndex >= 0 ? href.slice(0, hashIndex) : href;
  const queryIndex = withoutHash.indexOf('?');
  const path = queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash;
  const existing = new URLSearchParams(queryIndex >= 0 ? withoutHash.slice(queryIndex + 1) : '');
  for (const key of keys) {
    if (!existing.has(key)) existing.set(key, extra[key]);
  }
  const query = existing.toString();
  return `${path}${query ? `?${query}` : ''}${hash}`;
}

/**
 * Path plus the params a /free click destination is allowed to carry:
 * mode, redirect, autostart, source, src, and the utm allowlist.
 * Address-like values are dropped, including on redirect and source.
 * Values over FREE_PAGE_QUERY_VALUE_MAX_LENGTH are dropped, not truncated.
 */
export function freeCtaDestination(href: string): string {
  const hashIndex = href.indexOf('#');
  const withoutHash = hashIndex >= 0 ? href.slice(0, hashIndex) : href;
  const queryIndex = withoutHash.indexOf('?');
  const path = (queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash).replace(/^https?:\/\/[^/]+/i, '');
  const incoming = new URLSearchParams(queryIndex >= 0 ? withoutHash.slice(queryIndex + 1) : '');
  const kept = new URLSearchParams();
  for (const [key, value] of incoming.entries()) {
    if (!isDestinationQueryKey(key)) continue;
    const accepted = acceptedFreeQueryValue(value);
    if (!accepted) continue;
    kept.append(key, accepted);
  }
  const query = kept.toString();
  return `${path}${query ? `?${query}` : ''}`;
}

export function freePageViewProperties(search: string): Record<string, string> {
  return {
    source_path: '/free',
    ...freePageAttribution(search),
  };
}

export function freeCtaClickProperties(input: {
  ctaId: string;
  href: string;
  search: string;
}): Record<string, string> {
  const attributed = withFreePageAttribution(input.href, input.search);
  return {
    cta_id: input.ctaId,
    destination: freeCtaDestination(attributed),
    ...freePageAttribution(input.search),
  };
}
